import { useState, type ReactNode } from 'react'
import { Button, Field, Input, Modal, Select, TextArea, type ButtonVariant } from '@/ui'
import { toCode } from '../lib/text'

export interface ReasonSpec {
  label?: ReactNode
  hint?: ReactNode
  placeholder?: string
  /** Required unless false. */
  required?: boolean
  minLength?: number
  /** Server format, e.g. /^[A-Z0-9_]{2,40}$/ for hold reasons. */
  pattern?: RegExp
  patternMessage?: string
  /** Offer fixed choices (still free text with `allowOther`). */
  choices?: Array<{ value: string; label: string }>
  allowOther?: boolean
  multiline?: boolean
  /** Upper-case and underscore the typed value (codes). */
  asCode?: boolean
}

export interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  title: ReactNode
  description?: ReactNode
  /** Extra content above the reason (what will happen, blockers…). */
  children?: ReactNode
  confirmLabel: string
  tone?: ButtonVariant
  reason?: ReasonSpec
  loading?: boolean
  /** Called with the trimmed reason ('' when no reason asked). */
  onConfirm: (reason: string) => void | Promise<unknown>
  /** Disable confirm with an explanation (e.g. two-person rule). */
  disabledReason?: string | null
}

/**
 * Confirmation with an optional reason, validated like the server validates it. Closing is
 * blocked while the request runs.
 */
export function ConfirmDialog({
  open,
  onClose,
  title,
  description,
  children,
  confirmLabel,
  tone = 'primary',
  reason,
  loading,
  onConfirm,
  disabledReason,
}: ConfirmDialogProps) {
  const [text, setText] = useState('')
  const [choice, setChoice] = useState('')
  const [touched, setTouched] = useState(false)
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setText('')
      setChoice('')
      setTouched(false)
    }
  }

  const raw = reason?.choices && choice && choice !== '__other' ? choice : text
  const value = reason?.asCode ? toCode(raw) : raw.trim()
  const required = reason ? reason.required !== false : false
  let error: string | null = null
  if (reason) {
    if (required && !value) error = 'Required'
    else if (value && reason.minLength && value.length < reason.minLength)
      error = `At least ${reason.minLength} characters`
    else if (value && reason.pattern && !reason.pattern.test(value))
      error = reason.patternMessage ?? 'Not in the expected format'
  }
  const blocked = Boolean(disabledReason) || Boolean(error)

  const submit = async () => {
    setTouched(true)
    if (blocked) return
    try {
      await onConfirm(value)
    } catch {
      /* the mutation already told the person (toast or inline) */
    }
  }

  const showText = !reason?.choices || choice === '__other' || (reason.allowOther && !reason.choices.length)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      dismissible={!loading}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={loading} className="ml-auto">
            Cancel
          </Button>
          <Button variant={tone} onClick={() => void submit()} loading={loading} disabled={Boolean(disabledReason)}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
      >
        {children}
        {disabledReason && (
          <p role="alert" className="rounded-card border border-warn/30 bg-warn-wash px-3 py-2 text-[14px] text-ink">
            {disabledReason}
          </p>
        )}
        {reason && (
          <>
            {reason.choices && (
              <Field label={reason.label ?? 'Reason'} required={required}>
                <Select
                  value={choice}
                  onChange={setChoice}
                  placeholder="Choose…"
                  options={[
                    ...reason.choices,
                    ...(reason.allowOther ? [{ value: '__other', label: 'Other…' }] : []),
                  ]}
                />
              </Field>
            )}
            {showText && (
              <Field
                label={reason.choices ? 'Other reason' : (reason.label ?? 'Reason')}
                hint={reason.asCode && value ? `Saved as ${value}` : reason.hint}
                error={touched ? error : null}
                required={required}
              >
                {reason.multiline !== false && !reason.asCode ? (
                  <TextArea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={reason.placeholder}
                    data-autofocus
                    rows={3}
                  />
                ) : (
                  <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={reason.placeholder} data-autofocus />
                )}
              </Field>
            )}
            {!showText && touched && error && (
              <p className="text-[13px] font-medium text-danger">{error}</p>
            )}
          </>
        )}
      </form>
    </Modal>
  )
}
